import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { haptic } from "../platform/telegram";
import "./styles/LanguageSwitcher.css";

const LANGUAGES = [
  { code: "en", name: "English", short: "EN" },
  { code: "ru", name: "Русский", short: "RU" },
];

const GlobeIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14.5 14.5 0 0 1 0 18 14.5 14.5 0 0 1 0-18z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const LanguageSwitcher: React.FC = memo(() => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);

  const current = (i18n.language || "en").slice(0, 2);

  const close = useCallback(() => setOpen(false), []);

  const toggle = useCallback(() => {
    haptic("light");
    setOpen((prev) => !prev);
  }, []);

  const handleLanguageChange = useCallback(
    (langCode: string) => {
      i18n.changeLanguage(langCode);
      haptic("light");
      setOpen(false);
    },
    [i18n]
  );

  // Close the menu on Escape for keyboard users.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className={`language-switcher${open ? " open" : ""}`}>
      <button
        type="button"
        className="language-switcher-btn"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("common.language")}
        title={t("common.language")}
      >
        <GlobeIcon />
      </button>

      {open && (
        <>
          <div className="language-sheet-scrim" onClick={close} />
          <div
            className="language-menu"
            role="menu"
            aria-label={t("common.language")}
          >
            <div className="language-menu-handle" aria-hidden="true" />
            <div className="language-menu-title">{t("common.language")}</div>
            {LANGUAGES.map((lang) => {
              const selected = lang.code === current;
              return (
                <button
                  key={lang.code}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  className={`language-menu-item${selected ? " active" : ""}`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <span className="language-menu-code">{lang.short}</span>
                  <span className="language-menu-name">{lang.name}</span>
                  {selected && (
                    <span className="language-menu-check">
                      <CheckIcon />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
});

LanguageSwitcher.displayName = "LanguageSwitcher";

export default LanguageSwitcher;
